import React, { useState, useCallback, useEffect } from "react";
import { Row, Col, Pagination, Collapse, message, Button, Modal } from "antd";
import axios from "axios";
import { CaretRightOutlined, DownloadOutlined } from "@ant-design/icons";

import PageHeader from "../Layout/PageHeader";
import VoucherRedemptionList from "./VoucherRedemptionList";
import VoucherRedemptionFilters from "./VoucherRedemptionFilters";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const VoucherRedemption = () => {
  const { permissions } = usePermissions();

  const [loading, setLoading] = useState({ index: false, export: false });
  const [voucherRedemptions, setVoucherRedemptions] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });

  const fetchVoucherRedemptions = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, index: true }));

    const params = {
      limit: pagination.limit,
      page: pagination.currentPage,
      ...(filters.search && {
        searchField: filters.searchField,
        search: filters.search,
      }),
      ...(filters.status && {
        status: filters.status,
      }),
      dispatchStartDate: filters.dispatchStartDate
        ?.startOf("day")
        ?.toISOString(),
      dispatchEndDate: filters.dispatchEndDate?.endOf("day")?.toISOString(),
      redeemStartDate: filters.redeemStartDate?.startOf("day")?.toISOString(),
      redeemEndDate: filters.redeemEndDate?.endOf("day")?.toISOString(),
      sort: filters.sort,
    };
    const res = await axios.get("/api/vouchers/redemptions", { params });

    setVoucherRedemptions(res.data.data || []);
    setPagination(res.data.pagination);
    setLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [pagination.currentPage, pagination.limit, filters]);

  const exportDownload = async () => {
    setLoading((prevLoading) => ({ ...prevLoading, export: true }));

    const params = {
      ...(filters.search && {
        searchField: filters.searchField,
        search: filters.search,
      }),
      ...(filters.status && {
        status: filters.status,
      }),
      dispatchStartDate: filters.dispatchStartDate
        ?.startOf("day")
        ?.toISOString(),
      dispatchEndDate: filters.dispatchEndDate?.endOf("day")?.toISOString(),
      redeemStartDate: filters.redeemStartDate?.startOf("day")?.toISOString(),
      redeemEndDate: filters.redeemEndDate?.endOf("day")?.toISOString(),
    };

    const messageContent = (
      <p>
        Are you sure you want to download {pagination.totalSize} data ?<br />
      </p>
    );

    Modal.confirm({
      content: messageContent,
      title: `Download Voucher History List ? `,
      async onOk() {
        await axios
          .get("/api/vouchers/redemptions", {
            params,
            headers: { accept: "application/vnd.ms-excel" },
          })
          .then((res) => {
            message.info(`Please check your email after 5-20 minute`);
          })
          .catch((error) => {
            message.error(error.message);
          });
      },
    });

    setLoading((prevLoading) => ({ ...prevLoading, export: false }));
  };

  useEffect(() => {
    fetchVoucherRedemptions();
  }, [pagination.currentPage, pagination.limit, fetchVoucherRedemptions]);

  return (
    <div>
      <PageHeader
        title="Voucher History"
        childRoutes={[
          {
            path: "/voucher/history",
            breadcrumbName: "Voucher History",
          },
        ]}
      />

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <VoucherRedemptionFilters
            filters={filters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row
        className="mt-2 mb-2"
        justify={
          permissions?.includes(PERMISSIONS.EXPORT_VOUCHER_REDEMPTION_LIST)
            ? "space-between"
            : "end"
        }
        align="middle"
      >
        {permissions?.includes(PERMISSIONS.EXPORT_VOUCHER_REDEMPTION_LIST) && (
          <span color="blue">
            <Button onClick={exportDownload} loading={loading.export}>
              Export <DownloadOutlined />
            </Button>
          </span>
        )}
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <VoucherRedemptionList
        voucherRedemptions={voucherRedemptions}
        loading={loading.index}
      />
      <Row className="mt-2 mb-2" justify="end" align="middle">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) =>
              setPagination({ ...pagination, limit, currentPage })
            }
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </div>
  );
};

export default VoucherRedemption;
